import { Label } from "flowbite-react";
import FileManager from "./FileManager";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { Attachment } from "../types/attachment";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { TINYMCE_API_KEY } from "../constants";
import { Editor } from '@tinymce/tinymce-react';
import SlideshowManager from "./SlideshowManager";

interface Props {
    label: string;
    editorRef: React.MutableRefObject<any>;
    content: string;
    onInit?:(editorRef: any) => void;
}
interface State {
    showFileManager: boolean;
    showModal: boolean;
    initialized: boolean;
    showSlideshowEditor: boolean;
    slideshowId: number;
}

const Wysiwyg = forwardRef(({label, editorRef, content, onInit} : Props, ref) => {
    const [state, setState] = useReducerAsState<State>({
        showFileManager: false,
        showModal: false,
        initialized: false,
        showSlideshowEditor: false,
        slideshowId: 0,
    })

    useImperativeHandle(ref, () => ({
        handleSlideshows(content: string){
            updateEditorContent(content)
        }
    }));
    
    const onInitEditor = async (evt: any,editor: any) => {
        editorRef.current = editor;
        if(onInit){
            onInit(editorRef.current)
        }

        // DC custom button to open file manager an embed images, videos or audios
        editorRef.current.ui.registry.addButton('monclovaMedia', {
            text: 'Multimedia',
            onAction : () => {
                setState({
                    showFileManager: true,
                })
            }
        })

        setState({initialized: true})
        // DC: Slideshow creator
        editorRef.current.ui.registry.addButton('monclovaSlideshow', {
            text: 'Slideshow',
            onAction : () => {
                setState({showSlideshowEditor: true, slideshowId: 0})
            }
        })
    }

    const handleCloseSlideshow = () => setState({showSlideshowEditor: false})

    const updateEditorContent =  (editorContent: string | undefined) => {
        if(!state.initialized) return

        if(!editorRef.current) return

        const content = editorContent ?? editorRef.current.getContent();

        // DC: search for all slideshows and replace with a button to edit them with any number length
        const slideshows = content.match(/<!-- monclova?-slideshow-\w+ -->/g)
        let updatedContent = content
        if(slideshows){
            slideshows.forEach((slideshow: string) => {
                const slideshowNumber = slideshow.match(/monclova-slideshow-(\w+)/)
                if(!slideshowNumber) return
                updatedContent = updatedContent.replace(slideshow, `<p><button data-slideshow-id="${slideshowNumber[1]}">EDITAR SLIDESHOW</button></p>`)
            })

            editorRef.current.setContent(updatedContent)

            // DC: add event listener to the button to open the slideshow editor
            const slideshowButtons = editorRef.current.getDoc().querySelectorAll('[data-slideshow-id]')
            slideshowButtons.forEach((slideshowButton: any) => {
                slideshowButton.addEventListener('click', (e: any) => {
                    const slideshowId = e.target.getAttribute('data-slideshow-id')
                    if(!slideshowId) return

                    // DC: open the slideshow editor
                    setState({showSlideshowEditor: true, slideshowId})
                })
            })
        }
    }

    const onSlideshowSaved = async (slideshowId: number) => {
        if(!slideshowId) return

        if(state.slideshowId === 0) {
            // DC: Creating slideshow
            const content = editorRef.current
            const p =  document.createElement("p")
            p.innerHTML = `<button data-slideshow-id="${slideshowId}">EDITAR SLIDESHOW</button></p>`
            content.insertContent(p.outerHTML)
            
            // DC: add event listener to the button to open the slideshow editor
            const slideshowButtons = editorRef.current.getDoc().querySelectorAll('[data-slideshow-id]')
            slideshowButtons.forEach((slideshowButton: any) => {
                slideshowButton.addEventListener('click', (e: any) => {
                    const slideshowId = e.target.getAttribute('data-slideshow-id')
                    if(!slideshowId) return

                    // DC: open the slideshow editor
                    setState({showSlideshowEditor: true, slideshowId})
                })
            })


            setState({showSlideshowEditor: false, slideshowId: 0})
            return
        }

        setState({showSlideshowEditor: false, slideshowId: 0})
    }

    const onDeleteSlideshow = async (slideshowId: number) => {
        if(!slideshowId) return

        const content = editorRef.current.getContent()
        const updatedContent = content.replace(`<p><button data-slideshow-id="${slideshowId}">EDITAR SLIDESHOW</button></p>`,'')
        editorRef.current.setContent(updatedContent)
        updateEditorContent(undefined)
        
        setState({showSlideshowEditor: false, slideshowId: 0})
    }

    useEffect(() => {   
        setTimeout(() => {
            updateEditorContent(undefined)
        },2000)
        // eslint-disable-next-line
    },[state.initialized])

    const onSelectFile = (file: Attachment) => {
        if(!file || file.type === 'pdf') return

        const fileUrl = `${process.env.REACT_APP_UPLOADS}/${file.url}`
        const p =  document.createElement("p")

        if(file.type === "image"){
            const image = document.createElement('img')
            image.src = fileUrl
            p.appendChild(image)
        }

        if(file.type === "video"){
            const video = document.createElement("video")
            video.src = fileUrl
            video.controls = true
            p.appendChild(video)
        }

        editorRef.current.insertContent(p.outerHTML)
        setState({ showFileManager: false })
    }

    /*const onSaveEmbed = async () => {
        if(!showModal && !embed && !editorRef.current) return

        if(embed.includes("instagram")){
            
            const parts = embed.split("/")
            if(parts.length === 0 || !parts.includes("p")) return
            const pIndex = parts.findIndex(item => item === "p");
            if(pIndex === -1) return

            const newIndex = pIndex + 1
            const postId = parts[newIndex]

            var iframe = document.createElement('iframe');
            iframe.src = 'https://www.instagram.com/p/' + postId + '/embed/';
            iframe.width = '500'; // Set the desired width of the iframe
            iframe.height = '600'; // Set the desired height of the iframe
            iframe.frameBorder = '0'; // Remove iframe border
            iframe.allowFullscreen = true; // All
            iframe.allow = "autoplay encrypted-media allow-scripts allow-popups allow-forms";
            //iframe.setAttribute("sandbox","allow-same-origin allow-scripts allow-popups allow-forms")
            //console.log(iframe,'HERE')
            //iframe.appendChild(embed as any)
            //console.log(iframe.outerHTML)
            editorRef.current.insertContent(iframe.outerHTML)
            return
        }

        const html = await requestEmbed(embed)
        if(!html) return

        editorRef.current.insertContent(html)
    }*/

    /*const setEditorContent = (content: string) => {
        if(!editorRef.current) return
        editorRef.current.insertContent(content)
    }

    useEffect(() => {
        if(!content) return
        setEditorContent(content)
    },[content])*/

    const handleFileManagerClose = () => setState({ showFileManager: false }) // setShowFileManager(false)

    // pageembed     "@tinymce/tinymce-react": "^5.0.1",

    return <div className="flex flex-col mb-2">
        <Label className="mb-2">{label}</Label>
       
        <Editor
            tinymceScriptSrc='/tinymce/tinymce.min.js'
            licenseKey="gpl"
            onInit={(_evt, editor) => onInitEditor(_evt,editor)}
            initialValue={content}
            init={{
                height: 640,
                menubar: true,
                mediaembed_inline_styles: true,
                plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                ],
                toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor blockquote | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help | monclovaMedia | monclovaSlideshow',
                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
            }}
        />
        {state.showFileManager && <FileManager open={state.showFileManager} onClose={handleFileManagerClose} onSelect={onSelectFile} />}
        {state.showSlideshowEditor && <SlideshowManager open={state.showSlideshowEditor} slideshowId={state.slideshowId} onClose={handleCloseSlideshow} onSave={onSlideshowSaved} onDelete={onDeleteSlideshow} />}  
    </div>
});

export default Wysiwyg;